/**
 * Scheda profilo medico con ripartizione oraria e flag
 */

import {
  DISTRIBUTION_AXIS_ORDER,
  DISTRIBUTION_AXIS_LABELS,
  DEFAULT_WEEKLY_HOURS,
  defaultDistribuzioneFiveAxes,
  ABSENCE_CATEGORIES,
  ABSENCE_CATEGORY_LABELS,
  SLOT_ABSENCE_TYPES,
  SLOT_ABSENCE_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
} from '../types.js';
import {
  getBimesterForDate,
  getAdjacentBimester,
} from '../workload.js';
import {
  getDoctor,
  updateDoctor,
  addDoctor,
  deleteDoctor,
  slotsFromPercentage,
  migrateDoctorDistribuzione,
  getRecurringExclusionsForDoctor,
  addRecurringExclusion,
  deleteRecurringExclusion,
} from '../store.js';

const DOCTOR_WEEKLY_DEBITO_MIN = 0;
const DOCTOR_WEEKLY_DEBITO_MAX = 40;
/** Riporto ore di apertura bimestre: limite di sicurezza sull'input (ore). */
const DOCTOR_SALDO_APERTURA_MAX = 999;

/** Bimestri selezionabili per il riporto: da 2 prima a 4 dopo quello corrente. */
function bimesterOptions() {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const cur = getBimesterForDate(today);
  if (!cur) return [];
  const out = [];
  for (let delta = -2; delta <= 4; delta++) {
    const b = getAdjacentBimester(cur.year, cur.index, delta);
    out.push({ key: b.key, label: b.label });
  }
  return out;
}

/** Legge dal form il riporto di apertura. Senza bimestre selezionato il riporto non si applica. */
function openingBalanceFromInput(container) {
  const bimestre = String(container.querySelector('#input-saldo-bimestre')?.value ?? '').trim();
  if (!bimestre) return { saldoOreApertura: 0, saldoOreAperturaBimestre: null };
  const raw = String(container.querySelector('#input-saldo-apertura')?.value ?? '').trim().replace(',', '.');
  const n = raw === '' ? 0 : Number(raw);
  const ore = Number.isFinite(n) ? Math.max(-DOCTOR_SALDO_APERTURA_MAX, Math.min(DOCTOR_SALDO_APERTURA_MAX, Math.round(n * 10) / 10)) : 0;
  return { saldoOreApertura: ore, saldoOreAperturaBimestre: bimestre };
}

function weeklyDebitoFromInput(container) {
  const el = container.querySelector('#input-debito');
  if (!el) return null;
  const n = el.valueAsNumber;
  if (Number.isFinite(n) && n >= DOCTOR_WEEKLY_DEBITO_MIN && n <= DOCTOR_WEEKLY_DEBITO_MAX) {
    return Math.round(n);
  }
  const parsed = parseInt(String(el.value).trim(), 10);
  if (Number.isFinite(parsed) && parsed >= DOCTOR_WEEKLY_DEBITO_MIN && parsed <= DOCTOR_WEEKLY_DEBITO_MAX) {
    return parsed;
  }
  return null;
}

const template = document.createElement('template');
template.innerHTML = `
  <div class="card doctor-profile">
    <div class="modal-header">
      <h2 id="profile-title">Nuovo Medico</h2>
      <button class="btn btn-secondary btn-sm" id="btn-close">Chiudi</button>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label>Nome</label>
        <input type="text" id="input-nome" placeholder="Nome">
      </div>
      <div class="form-group">
        <label>Cognome</label>
        <input type="text" id="input-cognome" placeholder="Cognome">
      </div>
      <div class="form-group">
        <label>Sigla (univoca, 2-4 caratteri)</label>
        <input type="text" id="input-sigla" placeholder="es. MR" maxlength="4">
      </div>
      <div class="form-group">
        <label>Debito orario settimanale (ore)</label>
        <input type="number" id="input-debito" min="0" max="40" step="1" title="Ore contrattuali a settimana (0 = nessun debito clinico; es. 38 full-time, 36 o 30 part-time). Ore/giorno = debito÷5; vincoli e Carichi usano questo valore.">
      </div>

      <div class="card-title" style="margin-top: 1.5rem;">Ripartizione competenze (Piano turni)</div>
      <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;">
        Cinque assi: TI (feriali diurni), Formazione, Sala operatoria/NORA (condivisi), Medicina del dolore, Cure palliative. Somma = 100%. Gli slot indicano i turni settimanali corrispondenti.
      </p>
      <div id="sliders-container"></div>
      <div style="font-size: 0.85rem; margin-top: 0.5rem;">
        Totale: <strong id="total-percent">100</strong>%
      </div>

      <div class="card-title" style="margin-top: 1.5rem;" id="recurring-section-title">Impegni ricorrenti</div>
      <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;" id="recurring-section-desc">
        Libera professione o permessi fissi (es. assistenza familiari) che si ripetono ogni settimana. Vengono riversati automaticamente sul calendario assenze fino a cancellazione.
      </p>
      <div id="recurring-exclusions-list"></div>
      <div id="recurring-add-form" style="display: none; margin-top: 1rem; padding: 1rem; background: var(--bg-secondary); border-radius: 8px;">
        <div class="form-group">
          <label>Tipo</label>
          <select id="rec-category">
            <option value="${ABSENCE_CATEGORIES.LIBERA_PROFESSIONE}">Libera Professione</option>
            <option value="${ABSENCE_CATEGORIES.PERMESSO}">Permesso (es. assistenza familiari)</option>
          </select>
        </div>
        <div class="form-group">
          <label>Giorno settimana</label>
          <select id="rec-weekday">
            ${WEEKDAYS.map(w => `<option value="${w}">${WEEKDAY_LABELS[w]}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" id="rec-slots-group">
          <label>Slot esclusi</label>
          <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
            ${SLOT_ABSENCE_TYPES.map(s => `<label class="checkbox-item"><input type="checkbox" class="rec-slot" value="${s}"> ${SLOT_ABSENCE_LABELS[s]}</label>`).join('')}
          </div>
        </div>
        <div class="form-group" style="display: flex; gap: 0.5rem;">
          <div>
            <label>Valido da (opz.)</label>
            <input type="date" id="rec-date-from" placeholder="YYYY-MM-DD">
          </div>
          <div>
            <label>Valido a (opz.)</label>
            <input type="date" id="rec-date-to" placeholder="YYYY-MM-DD">
          </div>
        </div>
        <div style="display: flex; gap: 0.5rem; margin-top: 0.5rem;">
          <button class="btn btn-primary btn-sm" id="rec-add-btn">Aggiungi</button>
          <button class="btn btn-secondary btn-sm" id="rec-cancel-btn">Annulla</button>
        </div>
      </div>
      <button class="btn btn-secondary btn-sm" id="rec-show-form-btn" style="margin-top: 0.5rem;">+ Aggiungi impegno ricorrente</button>

      <div class="card-title" style="margin-top: 1.5rem;">Opzioni</div>
      <div class="checkbox-group">
        <label class="checkbox-item">
          <input type="checkbox" id="check-esenzione-notti">
          <span>Esenzione notti e reperibilità</span>
        </label>
        <label class="checkbox-item">
          <input type="checkbox" id="check-esenzione-weekend">
          <span>Esenzione weekend (diurni e notti)</span>
        </label>
        <label class="checkbox-item">
          <input type="checkbox" id="check-esenzione-cliniche">
          <span>Esenzione attività cliniche</span>
        </label>
        <label class="checkbox-item">
          <input type="checkbox" id="check-direzione">
          <span>Abilità Direzione</span>
        </label>
        <label class="checkbox-item">
          <input type="checkbox" id="check-specializzando">
          <span>Specializzando (no notti, festivi, reperibilità)</span>
        </label>
      </div>

      <div class="form-group" style="margin-top: 1rem;">
        <label>Data fine rapporto (cessazione incarico)</label>
        <input type="date" id="input-data-fine-rapporto" placeholder="YYYY-MM-DD">
        <p style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;">Opzionale. Se impostata, il medico non verrà conteggiato per ore dovute nelle settimane successive.</p>
      </div>

      <div class="form-group" style="margin-top: 1rem;">
        <label>Riporto ore a inizio bimestre</label>
        <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center;">
          <input type="number" id="input-saldo-apertura" step="0.1" min="-999" max="999" placeholder="0" style="max-width: 8rem;" title="Ore di credito (positive) o debito (negative) con cui il medico apre il bimestre.">
          <select id="input-saldo-bimestre" title="Bimestre a cui si applica il riporto."></select>
        </div>
        <p style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;">Opzionale. Negativo = debito da recuperare, positivo = credito. Si applica <strong>solo</strong> al bimestre selezionato: senza bimestre il riporto non viene conteggiato.</p>
      </div>

      <div class="modal-footer" style="margin-top: 1.5rem; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem;">
        <div>
          <button class="btn btn-secondary" id="btn-cancel">Annulla</button>
          <button class="btn btn-primary" id="btn-save">Salva</button>
        </div>
        <button class="btn btn-danger btn-sm" id="btn-delete-doctor" style="display: none; margin-left: auto;">Elimina medico</button>
      </div>
    </div>
  </div>
`;

export function renderDoctorProfile(container, doctorId = null, onClose) {
  const doctor = doctorId ? getDoctor(doctorId) : null;
  const isNew = !doctor;

  container.innerHTML = '';
  container.appendChild(template.content.cloneNode(true));

  const slidersContainer = container.querySelector('#sliders-container');
  const totalEl = container.querySelector('#total-percent');
  const titleEl = container.querySelector('#profile-title');

  const areas = [...DISTRIBUTION_AXIS_ORDER];
  let values = doctor
    ? { ...migrateDoctorDistribuzione(doctor.distribuzione) }
    : { ...defaultDistribuzioneFiveAxes() };

  function normalizeSum(target = 100) {
    const sum = areas.reduce((a, k) => a + values[k], 0);
    if (sum === 0) return;
    const factor = target / sum;
    const next = {};
    let remainder = target;
    areas.forEach((k, i) => {
      const v = i < areas.length - 1
        ? Math.round(values[k] * factor)
        : remainder;
      next[k] = Math.max(0, Math.min(100, v));
      remainder -= next[k];
    });
    next[areas[areas.length - 1]] = Math.max(0, target - areas.slice(0, -1).reduce((a, k) => a + next[k], 0));
    values = next;
  }

  function renderSliders() {
    const debito = weeklyDebitoFromInput(container) ?? DEFAULT_WEEKLY_HOURS;
    slidersContainer.innerHTML = areas.map(area => {
      const v = values[area];
      const slots = slotsFromPercentage(v, debito);
      return `
        <div class="slider-group" data-area="${area}">
          <label>
            <span>${DISTRIBUTION_AXIS_LABELS[area]}</span>
            <span class="slots-info">≈ ${slots} slot/settimana</span>
          </label>
          <input type="range" min="0" max="100" value="${v}" data-area="${area}">
        </div>
      `;
    }).join('');

    totalEl.textContent = areas.reduce((a, k) => a + values[k], 0);

    slidersContainer.querySelectorAll('input[type="range"]').forEach(input => {
      input.addEventListener('input', e => {
        const area = e.target.dataset.area;
        const newVal = parseInt(e.target.value, 10);
        const others = areas.filter(a => a !== area);
        const rest = 100 - newVal;
        const otherSum = others.reduce((a, k) => a + values[k], 0);
        values[area] = newVal;
        if (otherSum > 0 && rest >= 0) {
          others.forEach(k => {
            values[k] = Math.round((values[k] / otherSum) * rest);
          });
          values[others[others.length - 1]] = Math.max(0, rest - others.slice(0, -1).reduce((a, k) => a + values[k], 0));
        }
        renderSliders();
      });
    });
  }

  const debitoInput = container.querySelector('#input-debito');
  debitoInput.value = String(doctor?.debitoOrarioSettimanale ?? DEFAULT_WEEKLY_HOURS);

  const saldoBimestreSel = container.querySelector('#input-saldo-bimestre');
  if (saldoBimestreSel) {
    const opts = bimesterOptions();
    const current = doctor?.saldoOreAperturaBimestre ?? '';
    if (current && !opts.some(o => o.key === current)) opts.unshift({ key: current, label: current });
    saldoBimestreSel.innerHTML = ['<option value="">— nessun riporto —</option>']
      .concat(opts.map(o => `<option value="${o.key}">${o.label}</option>`))
      .join('');
    saldoBimestreSel.value = current;
  }
  const saldoOreInput = container.querySelector('#input-saldo-apertura');
  if (saldoOreInput) {
    const v = Number(doctor?.saldoOreApertura ?? 0) || 0;
    saldoOreInput.value = v === 0 ? '' : String(v);
  }

  if (doctor) {
    titleEl.textContent = `${doctor.nome} ${doctor.cognome} (${doctor.sigla})`;
    container.querySelector('#input-nome').value = doctor.nome;
    container.querySelector('#input-cognome').value = doctor.cognome;
    container.querySelector('#input-sigla').value = doctor.sigla;
    container.querySelector('#check-esenzione-notti').checked = doctor.esenzioneNotti;
    container.querySelector('#check-esenzione-weekend').checked = doctor.esenzioneWeekend ?? false;
    container.querySelector('#check-esenzione-cliniche').checked = doctor.esenzioneAttivitaCliniche;
    container.querySelector('#check-direzione').checked = doctor.abilitaDirezione;
    container.querySelector('#check-specializzando').checked = doctor.specializzando ?? false;
    container.querySelector('#input-data-fine-rapporto').value = doctor.dataFineRapporto || '';
    // Mostra sezione impegni ricorrenti solo per medico esistente
    container.querySelector('#recurring-section-title').style.display = '';
    container.querySelector('#recurring-section-desc').style.display = '';
    container.querySelector('#recurring-exclusions-list').style.display = '';
    container.querySelector('#recurring-add-form').style.display = 'none';
    container.querySelector('#rec-show-form-btn').style.display = '';
    container.querySelector('#btn-delete-doctor').style.display = '';
    setupRecurringSection(container, doctorId);
  } else {
    titleEl.textContent = 'Nuovo Medico';
    container.querySelector('#recurring-section-title').style.display = 'none';
    container.querySelector('#recurring-section-desc').style.display = 'none';
    container.querySelector('#recurring-exclusions-list').style.display = 'none';
    container.querySelector('#rec-show-form-btn').style.display = 'none';
  }

  debitoInput.addEventListener('input', renderSliders);
  debitoInput.addEventListener('change', renderSliders);
  renderSliders();

  container.querySelector('#btn-save').addEventListener('click', async () => {
    const nome = container.querySelector('#input-nome').value.trim();
    const cognome = container.querySelector('#input-cognome').value.trim();
    const sigla = container.querySelector('#input-sigla').value.trim().toUpperCase().slice(0, 4);
    const debito = weeklyDebitoFromInput(container);
    if (debito === null) {
      alert(`Debito orario non valido: inserisci un numero tra ${DOCTOR_WEEKLY_DEBITO_MIN} e ${DOCTOR_WEEKLY_DEBITO_MAX} ore settimanali.`);
      return;
    }
    if (!nome || !cognome || !sigla) {
      alert('Compila Nome, Cognome e Sigla.');
      return;
    }
    try {
      const saldoApertura = openingBalanceFromInput(container);
      if (isNew) {
        const dfr = container.querySelector('#input-data-fine-rapporto').value || null;
        await addDoctor({
          nome,
          cognome,
          sigla,
          debitoOrarioSettimanale: debito,
          ...saldoApertura,
          esenzioneNotti: container.querySelector('#check-esenzione-notti').checked,
          esenzioneWeekend: container.querySelector('#check-esenzione-weekend').checked,
          esenzioneAttivitaCliniche: container.querySelector('#check-esenzione-cliniche').checked,
          dataFineRapporto: dfr,
          abilitaDirezione: container.querySelector('#check-direzione').checked,
          specializzando: container.querySelector('#check-specializzando').checked,
          distribuzione: { ...values },
        });
      } else {
        const dfr = container.querySelector('#input-data-fine-rapporto').value || null;
        await updateDoctor(doctorId, {
          nome,
          cognome,
          sigla,
          debitoOrarioSettimanale: debito,
          ...saldoApertura,
          esenzioneNotti: container.querySelector('#check-esenzione-notti').checked,
          esenzioneWeekend: container.querySelector('#check-esenzione-weekend').checked,
          esenzioneAttivitaCliniche: container.querySelector('#check-esenzione-cliniche').checked,
          dataFineRapporto: dfr,
          abilitaDirezione: container.querySelector('#check-direzione').checked,
          specializzando: container.querySelector('#check-specializzando').checked,
          distribuzione: { ...values },
        });
      }
      onClose?.();
    } catch (e) {
      alert('Errore salvataggio: ' + e.message);
    }
  });

  container.querySelector('#btn-close').addEventListener('click', onClose);
  container.querySelector('#btn-cancel').addEventListener('click', onClose);

  container.querySelector('#btn-delete-doctor')?.addEventListener('click', async () => {
    if (!doctor) return;
    const nomeCompleto = `${doctor.nome} ${doctor.cognome} (${doctor.sigla})`;
    const n = new Date();
    const oggi = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    const msg1 = `Cessazione rapporto di lavoro — ${nomeCompleto}\n\n`
      + `Indica l'ULTIMO giorno lavorato (formato AAAA-MM-GG).\n\n`
      + `Dal giorno successivo il medico:\n`
      + `• esce dall'elenco medici\n`
      + `• non riceve più notifiche e non può più accedere all'app\n`
      + `• non viene più assegnato, né dal solver né manualmente\n\n`
      + `Restano intatti tutti i suoi turni, le ore, il cognome e la sigla `
      + `nello storico e nella pagina Carichi.`;
    const dfr = prompt(msg1, doctor.dataFineRapporto || oggi);
    if (dfr === null) return;
    const dfrTrim = String(dfr).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dfrTrim)) {
      alert('Data non valida. Usa il formato AAAA-MM-GG.');
      return;
    }
    if (!confirm(`CONFERMA: ${nomeCompleto} risulterà cessato dal giorno dopo il ${dfrTrim}.\n\nProcedere?`)) return;
    try {
      await updateDoctor(doctorId, { dataFineRapporto: dfrTrim });
      onClose?.();
    } catch (e) {
      alert('Errore cessazione: ' + e.message);
    }
  });
}

function setupRecurringSection(container, doctorId) {
  function renderList() {
    const list = container.querySelector('#recurring-exclusions-list');
    const recs = getRecurringExclusionsForDoctor(doctorId);
    if (recs.length === 0) {
      list.innerHTML = '<p style="font-size: 0.9rem; color: var(--text-secondary);">Nessun impegno ricorrente. Aggiungine uno per libera professione o permessi fissi.</p>';
      return;
    }
    list.innerHTML = `
      <ul style="list-style: none; padding: 0; margin: 0;">
        ${recs.map(r => {
          const catLabel = ABSENCE_CATEGORY_LABELS[r.category] || r.category;
          const dayLabel = WEEKDAY_LABELS[r.weekday] || r.weekday;
          const slotStr = (r.slotTypes || []).length > 0
            ? ' (' + (r.slotTypes || []).map(s => SLOT_ABSENCE_LABELS[s] || s).join(', ') + ')'
            : '';
          const rangeStr = r.dateFrom || r.dateTo
            ? ` ${r.dateFrom || '…'} – ${r.dateTo || '…'}`
            : '';
          return `
          <li style="display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid var(--border);">
            <span>${catLabel} – ${dayLabel}${slotStr}${rangeStr}</span>
            <button class="btn btn-secondary btn-sm btn-del-rec" data-id="${r.id}">Rimuovi</button>
          </li>`;
        }).join('')}
      </ul>
    `;
    list.querySelectorAll('.btn-del-rec').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (!confirm('Rimuovere questo impegno ricorrente? Non verrà più riversato sul calendario.')) return;
        await deleteRecurringExclusion(btn.dataset.id);
        renderList();
      });
    });
  }

  const addForm = container.querySelector('#recurring-add-form');
  const showBtn = container.querySelector('#rec-show-form-btn');
  const categorySelect = container.querySelector('#rec-category');
  const slotsGroup = container.querySelector('#rec-slots-group');

  showBtn.addEventListener('click', () => {
    addForm.style.display = addForm.style.display === 'none' ? 'block' : 'none';
    showBtn.style.display = addForm.style.display === 'block' ? 'none' : '';
  });

  container.querySelector('#rec-cancel-btn').addEventListener('click', () => {
    addForm.style.display = 'none';
    showBtn.style.display = '';
  });

  categorySelect.addEventListener('change', () => {
    slotsGroup.style.display = categorySelect.value === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE ? 'block' : 'none';
  });

  container.querySelector('#rec-add-btn').addEventListener('click', async () => {
    const category = categorySelect.value;
    const weekday = container.querySelector('#rec-weekday').value;
    let slotTypes = [];
    if (category === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE) {
      container.querySelectorAll('.rec-slot:checked').forEach(cb => slotTypes.push(cb.value));
      if (slotTypes.length === 0) {
        alert('Seleziona almeno uno slot per Libera Professione.');
        return;
      }
    }
    const dateFrom = container.querySelector('#rec-date-from').value || null;
    const dateTo = container.querySelector('#rec-date-to').value || null;
    try {
      await addRecurringExclusion({
        doctorId,
        category,
        weekday,
        slotTypes,
        dateFrom,
        dateTo,
      });
      container.querySelector('#rec-date-from').value = '';
      container.querySelector('#rec-date-to').value = '';
      container.querySelectorAll('.rec-slot').forEach(cb => { cb.checked = false; });
      addForm.style.display = 'none';
      showBtn.style.display = '';
      renderList();
    } catch (e) {
      alert('Errore: ' + e.message);
    }
  });

  renderList();
}
